import { FC, memo, ReactElement, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { Colors, FlexBox, MEDIA, Skeleton, SPACING, useBreakpoint as useBreakpointClient } from '@brg/ui';
import styled, { useTheme, css } from 'styled-components';
import { Project } from '@brg/api';
import { AppTester } from '../../../supporting';
import { ProjectImageWrapper, ProjectLongreadWrapper } from '../../pages/project/styles';
import { ProjectContainerLayout } from './project-container-layout';
import ProjectHeader from '../../pages/project/components/ProjectHeader';
import TagsMobile from "../../pages/project/components/TagsMobile";
import { BTag } from "../../../core/components";
import { LayoutConst } from "../../../core";
import { useParams, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {CreateExternalLinkTo, LinkTo} from 'shared/constants/linkTo';
import { MainBookTitle } from '_deziiign/books/pages/BookView/components/MainBookTitle';
import { TagsMobBtn } from '../TagsMobBtn/TagsMobBtn';
import { useSelector } from "react-redux";
import { VOTE_BLOCK_POSITION } from 'shared/pages/project/sections/ps-bottom';
import VoteBlock from 'shared/pages/project/components/VoteBlock/VoteBlock';
import { MaskSwitcher } from 'shared/pages/project/components/MaskSwitcher';
import { useGetCard, useGetCards, useGetCardsList } from 'shared/hooks/api/useGetCards';
import {BTagsGridNew} from "../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import {isServer} from "../../constants/isServer";
import {useParamsSsr, useRouteMatchSsr} from "../../hooks/useRouteMatchSsr";
import { SP_VOTE_COLLECTION_SIGN } from 'shared/pages/VotesAdmin/constants';
import { BlockedProjectBanner } from 'shared/pages/project/components/BlockedProjectBanner';
import { isFtblMatchProject } from 'shared/constants/ftblProjectTypes';
import { RelatedProductsBlock, hasRelatedProductsContent } from './RelatedProductsBlock';
import { MediiiaContextHomeBanner } from './MediiiaContextHomeBanner';
import { ProjectTranslatedBanner } from 'shared/pages/project/components/ProjectTranslatedBanner';
import { getRepBaseByProvider } from 'shared/helpers/shopCardContentDisplayUrl';

//const TAGS_COUNT_IF_NEED_SCROLL = 7;

export interface ProjectDisplayLayoutProps {
  image?: ReactElement;
  firstLine?: ReactElement;
  contextName?: ReactElement;
  canViewStatus?: boolean;
  title?: ReactElement;
  header?: ReactElement;
  tags?: ReactElement[] | ReactElement;
  editorTags?: ReactElement;
  purchase?: ReactElement;
  point?: ReactElement;
  team?: ReactElement;
  dialog?: ReactElement;
  longread?: ReactElement;
  moreTitle?: ReactElement;
  studentProject?: ReactElement;
  more?: ReactElement;
  bottom?: ReactElement;
  afterContainerLayout?: ReactElement;
  likesSticker?: ReactElement;
  inPartCompetition?: ReactElement;
  inPartExam?: ReactElement;
  status?: ReactElement;
  placeElement?: ReactElement;
  protectStatus?: ReactElement;
  bookmarkButtonMobile?: ReactElement;
  subscribeInfo?: ReactElement;
  gameInfo?: ReactElement
  queue?: ReactElement
  LMSLongreads?: ReactElement
  queueButtons?: ReactElement
  bookBuyProcessingComponent?: ReactElement
  className?: any;
  isLoading?: boolean;
  style?: any;
  project: Project;
  shouldHideTags?: boolean;
  isCollectionVote?: boolean;
  mobileOffset?: number;
  blockedState?: {
    isBlocked: boolean;
    reasons: number[];
    otherReason: string;
  };
  statusVisible?: boolean;
  showTwoColumnsCriterias?: boolean;
  isOwner?: boolean;
  bgColor?: string;
  isLongTitle?: boolean;
  setShowVoteEndState?: () => void;
}

const SHORT_TITLE_TAGS_WINDOW_HEIGHT = 114;
const SHORT_TITLE_TAGS_WINDOW_HEIGHT_SMALL = 98;
const LONG_TITLE_TAGS_WINDOW_HEIGHT = 72;
const LONG_TITLE_TAGS_WINDOW_HEIGHT_SMALL = 62;
const LONG_TITLE_TAGS_WINDOW_HEIGHT_BELOW_1200 = 98;

const ProjectTitleSection = styled(FlexBox)`
  margin-bottom: 24px;
    
  @media(max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      margin-bottom: 16px;
  }
`;

const ProjectLeftSectionOuterMobile = styled.div<{ isAgeytomesh?: boolean; isFtblMatch?: boolean }>`
  position: relative;
  height: 100%;

  .ProjectHeaderOuterMobile {
    width: 100%;
  }

  .ProjectHeaderTitleMobile {
    font-weight: 500;
    font-size: 24px;
    line-height: 26px;
    padding: 0 10px;
    margin-top: ${({ isFtblMatch }) => isFtblMatch ? SPACING.S_8 : SPACING.S_10}px;

    @media(max-width: ${LayoutConst.screen.md}px) {
      padding-left: 0;

      ${({ isAgeytomesh }) => isAgeytomesh ? css`
        color: #545453;
        font-family: 'Inter';
        font-style: normal;
        font-weight: 500;
        font-size: 22px;
        line-height: 28px;
    ` : null};

    }
  }
`;

const MobileBookTitleOuter = styled(FlexBox)<{ hide?: boolean }>`
  display: flex;
  @media(max-width: ${LayoutConst.screen.md}px) {
    display: ${({ hide }) => hide ? 'none': "flex"};
  }
  @media(max-width: ${LayoutConst.screen.s - 1}px) {
    display: flex;
  }
`;

export const VoteLongreadBlockWrapper = styled(FlexBox)`
  width: 100%;
  margin: 0 auto;
  max-width: none;

  @media(max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    max-width: 888px;
  }

  @media(max-width: ${LayoutConst.screen.md}px) {
    width: calc(100% - 32px);
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
  }
`;

export const ProjectLongreadWrapperAfterContainer = styled(ProjectLongreadWrapper)`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
`;

const QueueButtonsAboveAfterContainerOuter = styled(FlexBox)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const AfterContainerLayoutOuter = styled(FlexBox)`
  display: none;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.l}px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;


export const BottomOuterMobile = styled.div`
  display: flex;
  width: 100%;

  .BottomOuterMobileLikes > div {
    flex-direction: row;
    align-items: center;
    gap: 16px;

    > div {
      margin-left: 0;
    }

    @media(max-width: ${LayoutConst.screen.md}px) {
      gap: 12px;
    }

    .buttons-wrapper {
      flex-direction: row;
      align-items: center;
      gap: 12px;

      .ps-action-btn {
        margin-left: 0 !important;
        margin-bottom: 0;
      }
    }

    .ShareButtonWrapper {
      margin-left: 0 !important;
    }
  }

  .StickyActionBarLikesControllerWrapper {
    margin-right: 8px;
  }

  .StickyActionBarShareButtonWrapper {
    margin-right: 4px;
  }

  .BottomOuterMobileActions {
    margin-left: auto;

    .MoreButtonWrapper {
      display: flex;
      padding: 0;
    }
  }
`;

export const QueueWrapper = styled(FlexBox)`
  @media(max-width: ${LayoutConst.screen.s -1 }px) {
    padding: 0 10px;
  }
`;

const RelatedTitle = styled.div`
    font-size: 20px;
    font-weight: 600;
    line-height: 25px; /* 125% */
    letter-spacing: -0.4px;
    color: ${({ theme }) => theme.relatedTitle};
    margin-top: 32px;
    margin-bottom: 16px;
    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
        font-size: 17px;
        line-height: 22px; /* 129.412% */
        letter-spacing: -0.34px; 
        margin-bottom: 8px;
        margin-top: 0px;  
    }

`;

export const MobBottomWrapper = styled(FlexBox)<{ shouldMobOffset?: boolean }>`
  width: 100%;

  @media(${MEDIA.MOBILE}) {
    margin-left: 0;
  }
    ${({ shouldMobOffset }) => 
      shouldMobOffset ? css`
        > div {
          padding-top: 24px;
        }
    ` : null};
`

const safeUseParams = isServer ? useParamsSsr : useParams
const useSafeRouteMatch = isServer ? useRouteMatchSsr : useRouteMatch
const useParamsProps = isServer ? LinkTo.project : undefined

const useBreakpoint = isServer ? () => false : useBreakpointClient;

const ProjectDisplayLayout: FC<ProjectDisplayLayoutProps> = (props) => {
  const {project, blockedState, setShowVoteEndState} = props
  const isMobile = useBreakpoint(940);
  const is834 = useBreakpoint(834);
  const is744 = useBreakpoint(LayoutConst.screen.s);
  const is743 = useBreakpoint(LayoutConst.screen.s - 1);
  const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
  const is1200 = useBreakpoint(LayoutConst.screen.l_12)
  const isBelow1200 = useBreakpoint(LayoutConst.screen.l);
  const theme = useTheme();
  const projectIdRef = useRef<{ id: string }>(null);
  const rightSideRef = useRef<HTMLDivElement>(null);
  const tagsContainerRef = useRef<HTMLDivElement>(null);
  const { id } = safeUseParams(useParamsProps);
  const isDeziiign = AppTester.isDeziiign();
  const [collapseTags, setCollapseTags] = useState(false);
  const [showMobileTags, setShowMobileTags] = useState(false);
  const isBookMainPage = useSafeRouteMatch('/books/book');
  const isBookProjectPage = useSafeRouteMatch('/book');
  const isBookPreview = useSafeRouteMatch('/books/preview');
  const isGeograffee = AppTester.isGeograffee();
  const isOnlines = AppTester.isOnlines();
  const isBooksGallllery = AppTester.isBooksGallllery()

  const isFtbl = AppTester.isFtbl()
  //TODO: [МУСОР] разобраться и удалить
  // const isBookEditPage = window.location.pathname.startsWith("/books/book-edit") || (AppTester.isBooksMain() && window.location.pathname.startsWith("/book-edit"))
  const isBookEditPage = false;
  const vtype = useSelector((state: any) => state.vt.projectVType);
  const { t, i18n } = useTranslation();

  const isProjectCardId = useMemo(() => {
      //@ts-ignore
    return props.project?.entitiesCreators?.[0]?.entityId ?? null;
  }, [props.project]);

  const hasProductCard = useMemo(() => project?.includedCards, [project]);
  //@ts-ignore
  const isReproduction = useMemo(() => project?.includedReproductionModels, [project]); 
  const isCardOfReproduction = isReproduction?.[0]?.cardId || '';

  const { card } = useGetCard(isCardOfReproduction || isProjectCardId);
  const { cards: allCards } = useGetCardsList(hasProductCard, !!hasProductCard)
  
  //@ts-ignore
  const isBookProject = useMemo(() => (props.project?.isBook && props.project?.paymentType === 1), [])

  const cards = useMemo(() => isBookProject && !isBooksGallllery? [{name: 'Электронная книга', cardId: props.project?.projectId}] : allCards ?? [], [allCards]);

  //TODO: добавить поля в интерфейс Project в api, чтобы избавиться от этих тс игноров 
  

  //@ts-ignore 
  const includedCollection = useMemo(() => project?.includedCardCollectionsIds, [project]);

  //@ts-ignore
  const includedCalendar = useMemo(() => project?.includedCalendarIds, [project]);

  const isTagsButtonVisible = useMemo(() => Array.isArray(props?.tags) ? props?.tags?.length > 0 : 3, [props?.tags])

  const isBook = useMemo(() => (isBookMainPage) , [isBookMainPage ])

  const isFtblMatch = useMemo(
    () => isFtblMatchProject(props.project?.projectType),
    [props.project?.projectType],
  );

  const smHeight = useMemo(() => (window.innerHeight < LayoutConst.screen.height.projectCover), [])

  const longTitleTagWindowHeightSmall = useMemo(() => {
    if (!props.isLongTitle) return undefined;
    return isBelow1200
      ? LONG_TITLE_TAGS_WINDOW_HEIGHT_BELOW_1200
      : LONG_TITLE_TAGS_WINDOW_HEIGHT_SMALL;
  }, [props.isLongTitle, isBelow1200]);

  const maxAllowedTagHeight = useMemo(() => {
    if (props.isLongTitle) {
      if (isBelow1200) return LONG_TITLE_TAGS_WINDOW_HEIGHT_BELOW_1200;
      if (isMac13 || smHeight) return LONG_TITLE_TAGS_WINDOW_HEIGHT_SMALL;
      return LONG_TITLE_TAGS_WINDOW_HEIGHT;
    }
    if (isMac13 || smHeight) return SHORT_TITLE_TAGS_WINDOW_HEIGHT_SMALL;
    return SHORT_TITLE_TAGS_WINDOW_HEIGHT;
  }, [props.isLongTitle, isBelow1200, isMac13, smHeight]);

  const collapsedTagWindowHeight = props.isLongTitle
    ? LONG_TITLE_TAGS_WINDOW_HEIGHT
    : SHORT_TITLE_TAGS_WINDOW_HEIGHT;

  const collapsedTagWindowHeightSmall = props.isLongTitle
    ? longTitleTagWindowHeightSmall
    : SHORT_TITLE_TAGS_WINDOW_HEIGHT_SMALL;

  const wx = useMemo(() =>  rightSideRef?.current?.clientWidth || '100%' , [rightSideRef, rightSideRef.current, isMac13, is743, isMobile, smHeight ])


  const handleOpenCardPage = () => {
    if (isReproduction?.length) {
      const baseUrl = getRepBaseByProvider(isReproduction?.[0]?.cardProviderId);
      if (!baseUrl) return;
      window.open(`${baseUrl}/typography/${isReproduction[0]?.cardId}`, '_blank');
    }
  };

  const handleOpenCalendarPage = () => {
    
    if (includedCalendar?.length) {
      window.open(`${process.env.REACT_APP_URL_GALLERY}/365/calendar/preview/${includedCalendar[0]}`, '_blank');
    }
  };

  const handleOpenCollectionPage = () => {
    
    if (includedCollection?.length) {
      window.open(`${process.env.REACT_APP_URL_GALLERY}/catalog/${includedCollection[0]}`, '_blank');
    }
  };

  const handleOpenProductPage = (card: any) : string => {
    if (isBookProject) {
      return CreateExternalLinkTo.mediiiaBooks(`/book/${props.project?.projectId}`)
    }
    if (hasProductCard?.length) {
      const cardId = card.entityId || card.cardId
      const baseUrl = `${process.env.REACT_APP_URL_ARTZ_SHOP}/shop`
      let fullUrl
      if (card?.shopDomain) {
        fullUrl = `https://${card.shopDomain}/card/${cardId}`
      } else {
        fullUrl = `${baseUrl}/card/${cardId}`
      }
      return fullUrl
    }
    return ''
  };

  const formatSequenceNumber = (sequenceNumber?: string): string => {
    if (!sequenceNumber) return '';
    
    const letters = sequenceNumber.match(/^[A-Za-z]+/)?.[0] || '';
    const digits = sequenceNumber.replace(letters, '').substring(0, 4);
    
    return `${letters} ${digits}`; 
  };

  const viewType = vtype || 'container';

  const shouldShowAfterContainerLayout = useMemo(
    () => (
      !!props.afterContainerLayout
      && !['fluid-wide', 't-shape', 'fixed-ratio-large', 'free-type'].includes(String(viewType))
    ),
    [props.afterContainerLayout, viewType],
  );

  const isPreview = !!useRouteMatch("/preview");

  const isMaskSwitcherVisibile = useMemo(
    () => (
      (props.project?.book && 
        !!props.project?.book?.isProtected && 
        (isPreview || isBookPreview) && 
        !!props.canViewStatus)
      ) , [props, isPreview, isBookPreview])

  const isAgeytomesh = AppTester.isAgeytomesh();
  const isDesign = AppTester.isDeziiign();
  const isHseDesign = AppTester.isHSEDesign();
  const isSuperPuper = AppTester.isSuperPuper();
  const isCGrave = AppTester.isCGRAVE();
  const isAdcrDafesNet = AppTester.isAdcrDafesNet();
  const isContinuumSite = AppTester.isContinuumSite();
  const isCompetition = AppTester.isCompetition();
  const isDafes = AppTester.isGeoDafes();

  useEffect(() => {
    if (props.isLoading) return;

    setCollapseTags(false);

    const getTagsMeasureElement = (): HTMLElement | null => {
      if (is743) {
        return document.querySelector('#mobile-tags-container') as HTMLElement | null;
      }

      return (
        (tagsContainerRef.current?.querySelector('#tags_grid_wrapper') as HTMLElement | null)
        ?? tagsContainerRef.current
      );
    };

    const measureTagsOverflow = () => {
      const tagsElement = getTagsMeasureElement();
      if (!tagsElement) return;

      setCollapseTags(tagsElement.scrollHeight > maxAllowedTagHeight + 1);
    };

    let rafId1 = 0;
    let rafId2 = 0;
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(measureTagsOverflow);
    });

    if (!is743) {
      [100, 300, 600, 1000].forEach((delay) => {
        timeoutIds.push(setTimeout(measureTagsOverflow, delay));
      });
      void document.fonts?.ready.then(() => {
        if (!cancelled) {
          measureTagsOverflow();
        }
      });
    }

    window.addEventListener('resize', measureTagsOverflow);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId1);
      cancelAnimationFrame(rafId2);
      timeoutIds.forEach(clearTimeout);
      window.removeEventListener('resize', measureTagsOverflow);
    };
  }, [
    id,
    is743,
    maxAllowedTagHeight,
    props.tags,
    props.isLongTitle,
    showMobileTags,
    props.isLoading,
  ]);


  //@ts-ignore
  projectIdRef.current = id;

  const handleToggleMobileTagClick = useCallback((flag: boolean) => setShowMobileTags(flag),  [])

  const calculateGradientColor = useMemo(() => {
    if (props.bgColor) {
      return props.bgColor
    }
    if (isSuperPuper || isCGrave || isContinuumSite || isAdcrDafesNet) {
      return '#000000';
    }
    if (isDesign || isHseDesign || isCompetition || isDafes ) {
      if (isBookEditPage || isMobile) return Colors.gal
      //@ts-ignore
      return props?.project?.additionalInfo?.color || Colors.gal
    }
    return Colors.geo; 
  }, [props?.project, isHseDesign, isDesign, isCompetition, isCGrave, isAdcrDafesNet , isContinuumSite, isMobile])

  const tagsGradientColor = calculateGradientColor;
  const relatedProductsProps = useMemo(
    () => ({
      cards,
      isReproduction,
      includedCollection,
      includedCalendar,
      isBookProject,
      shadowColor: tagsGradientColor,
      theme,
      t,
      i18n,
      handleOpenProductPage,
      handleOpenCardPage,
      handleOpenCollectionPage,
      handleOpenCalendarPage,
      formatSequenceNumber,
    }),
    [
      cards,
      isReproduction,
      includedCollection,
      includedCalendar,
      isBookProject,
      tagsGradientColor,
      theme,
      t,
      i18n,
      handleOpenProductPage,
      handleOpenCardPage,
      handleOpenCollectionPage,
      handleOpenCalendarPage,
      formatSequenceNumber,
    ],
  );
  const hasRelatedProducts = useMemo(
    () => hasRelatedProductsContent(relatedProductsProps),
    [relatedProductsProps],
  );
  const showHeaderOnTop = useMemo(() => (!props?.isCollectionVote), [props?.isCollectionVote])
  const isBookChapter = useMemo(() => (isBookPreview || props?.project?.book || (isPreview && !!props.project?.book)), [isBookPreview, isPreview, props.project])

  //Уберу , когда закончу с книгами @Artem
  const showDespLayout = useMemo(() => {
    if (isOnlines && !isMobile) return true;
    if (!isOnlines && !is743) return true;
    return false
  }, [isMobile, isOnlines, is744, is743, is834, isBook])


  //Уберу , когда закончу с книгами @Artem
  const showMobLayout = useMemo(() => {
    if (isOnlines && isMobile) return true;
    if (!isOnlines && is743) return true;
    return false
  }, [isOnlines, isMobile, is744, is834, is743, isBook ])
    const isCollectionVote = project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN)

  return (
    <FlexBox direction="column" directionMobile="column">
        {props.project && <BlockedProjectBanner 
          project={props.project} 
          isTemporaryBlocked={blockedState?.isBlocked}
          temporaryBlockingReasons={blockedState?.reasons}
          temporaryOtherReason={blockedState?.otherReason}
        />}
        {showDespLayout && (
        <ProjectContainerLayout
          footer={
            is744 && !isFtbl ?
            <>
                    <MobBottomWrapper 
                      directionMobile="column"
                      widthMobile={'100%'}
                      offsetLeftMobile={16}
                      shouldMobOffset={
                        isGeograffee && 
                        props.point && 
                        !showMobileTags
                      }
                    >
                            <RelatedProductsBlock {...relatedProductsProps} />
                      {isMaskSwitcherVisibile && <MaskSwitcher project={props?.project} minOffset={showMobileTags} />}
                    </MobBottomWrapper>
            </> :
            undefined
          }
          isProjectPage={!isBookMainPage && !isOnlines}
          isBookMainPage={!!isBookMainPage}
          isCollectionVote={isCollectionVote}
          header={props.header || <>
            {showHeaderOnTop && 
              <ProjectHeader 
                project={props.project} 
                fireClick={isPreview} 
                isProjectPage={!isBookMainPage && !isOnlines} 
                isBookPage={!!isBookMainPage} 
              />}
            </>}
          rightSide={
            <FlexBox
              direction="column"
              directionMobile="column"
              justify="space-between"
              width="100%"
              ref={rightSideRef}
              height="100%"
              style={{position: 'relative'}}
            >
              {!isMobile && props.placeElement && <div style={{position: 'absolute', right: is1200 ? '-34px' : '-64px'}}>{props.placeElement}</div>}
              <FlexBox direction="column" directionMobile='column'>
                {props?.bookBuyProcessingComponent ? props?.bookBuyProcessingComponent :  isDeziiign ? (
                  <>
                    <ProjectTitleSection gy={isBookChapter ? 0 : 8} direction={'column'} directionMobile='column' justify="space-between" align="flex-start">
                      {isBookChapter && (
                        <FlexBox width={'100%'} offsetBottom={16}>
                          <MainBookTitle book={props.project} />
                        </FlexBox>
                      )}
                      {props.isLoading ? (
                        <Skeleton
                          style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                          width={wx}
                          height={64}
                        />
                      ) : (
                        <FlexBox>
                          {props.title}
                        </FlexBox>
                      )}
                      {props?.firstLine && (
                        <FlexBox>
                            {props.firstLine}
                        </FlexBox>
                      )}
                    </ProjectTitleSection>

                    {props.isLoading ? (
                      <Skeleton
                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                        width={wx}
                        height={57}
                      />
                    ) : (
                        <>
                          <FlexBox direction='column' directionMobile='column' style={{ position: 'relative' }}>
                            <FlexBox gx={16} gxMobile={8} width="100%">
                              <div ref={tagsContainerRef} style={{ width: '100%' }}>
                                <BTagsGridNew
                                  id="tags_container"
                                  gap={10}
                                  scroll={collapseTags}
                                  shadow={collapseTags}
                                  isProjectPage
                                  noLimitMaxHeight={!collapseTags}
                                  tagWindowHeight={collapseTags ? collapsedTagWindowHeight : undefined}
                                  tagWindowHeightSmall={collapseTags ? collapsedTagWindowHeightSmall : undefined}
                                >
                                  {props.tags}
                                </BTagsGridNew>
                              </div>
                              {props?.editorTags || null}
                            </FlexBox>
                            {props.protectStatus}
                          </FlexBox>
                          {!is744 && <RelatedProductsBlock {...relatedProductsProps} />}
                          <MediiiaContextHomeBanner
                            hasProductsBelow={hasRelatedProducts && !is744}
                            project={props.project}
                          />
                        </>
                    )}
                  </>
                ) : (
                  <>
                    <FlexBox offsetBottom={(isMac13 && !isBookMainPage) ? 16 : !!isBookMainPage ? 24 : 16} gx={0} direction={'column'} directionMobile='column' justify="space-between" align="flex-start">
                      {isBookChapter && (
                        <FlexBox width={'100%'} offsetBottom={16}>
                          <MainBookTitle book={props.project} />
                        </FlexBox>
                      )}
                      <FlexBox offsetBottom={24} gx={8} justify="space-between" align="flex-start" width={'100%'}>
                        {props.isLoading ? (
                          <Skeleton
                            style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                            width={wx}
                            height={64}
                          />
                        ) : (
                          <FlexBox>
                            {props.title}
                          </FlexBox>
                        )}
                        {props?.firstLine && (
                          <FlexBox>
                            {props.firstLine}
                          </FlexBox>
                        )} 
                      </FlexBox>
                      <FlexBox width={'100%'}>
                        {props.isLoading ? (
                          <Skeleton
                            style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                            width={wx}
                            height={57}
                          />
                        ) : (
                          <FlexBox direction='column'>
                          {/*@ts-ignore*/}
                            <BTagsGridNew id={'tags_container'} gap={10} scrollMode="all" shadow>
                              {props.tags}
                            </BTagsGridNew>
                            {props.protectStatus}
                            {!!props.statusVisible && (
                              <FlexBox offsetTop={52}>
                                {props.status}
                              </FlexBox>
                            )}
                          </FlexBox>
                        )}
                      </FlexBox>
                      {!is744 && <RelatedProductsBlock {...relatedProductsProps} />}
                    </FlexBox>
                  </>
                )}
              </FlexBox>
              <FlexBox>
                {props.isLoading ? (
                  <Skeleton
                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                    width={wx}
                    height={32}
                  />
                ) : (
                  <FlexBox direction={isMaskSwitcherVisibile ? 'row' : 'column'} directionMobile={isMaskSwitcherVisibile ? 'row' : 'column'} width={'100%'}>
                    {props.bottom}
                    {isMaskSwitcherVisibile && <MaskSwitcher project={props?.project} />}
                  </FlexBox>
                )}
              </FlexBox>
            </FlexBox>
          }
          leftSide={
            <ProjectImageWrapper
              isProjectPage={!isBookMainPage && !isOnlines}
              isBooks={!!isBookMainPage}
              isFtblMatch={isFtblMatch}
            >
              {isMobile && props.placeElement && <div style={{position: 'absolute', left: '10px', top: '-3px'}}>{props.placeElement}</div>}
              {props.isLoading ? (
                <Skeleton
                  style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                  width="100%"
                  height="100%"
                />
              ) : (
                props.image
              )}
            </ProjectImageWrapper>
          }
        />
      )}

      {showDespLayout && !props.project?.isBook && (
        <VoteLongreadBlockWrapper justify="center" align="center">
          <VoteBlock
            setShowVoteEndState={setShowVoteEndState}
            project={props.project}
            fullSize
            position={VOTE_BLOCK_POSITION.afterContainer}
          />
        </VoteLongreadBlockWrapper>
      )}

      {showDespLayout && shouldShowAfterContainerLayout && props.queueButtons && (
        <QueueButtonsAboveAfterContainerOuter direction="column" directionMobile="column">
          {props.queueButtons}
        </QueueButtonsAboveAfterContainerOuter>
      )}

      {showDespLayout && shouldShowAfterContainerLayout && (
        <AfterContainerLayoutOuter direction="column" directionMobile="column">
          <ProjectLongreadWrapperAfterContainer
            showTags={showMobileTags}
            vtype={viewType}
            isBooks={!!isBookMainPage || !!isBookProjectPage}
          >
            {props.afterContainerLayout}
          </ProjectLongreadWrapperAfterContainer>
        </AfterContainerLayoutOuter>
      )}

      {showMobLayout && (
        <ProjectContainerLayout
          isBookMainPage={!!isBookMainPage}
          rightSide={

            <FlexBox direction="column" directionMobile="column" width="100%" ref={rightSideRef}>
              {isBookMainPage && isMobile && !is743 && (
                <div className="ProjectHeaderOuterMobile">
                  <div className="ProjectHeaderTitleMobile">
                    {props.title}
                  </div>
                </div>
              )}
              {!!isBookMainPage && isMobile && !is743 ? (
                <FlexBox offsetTopMobile={24}>
                  {props.isLoading ? (
                    <Skeleton
                      style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                      width={wx}
                      height={57}
                    />
                  ) : (
                    <FlexBox direction='column'>
                      <BTagsGridNew id="tags_container" scrollMode="all" shadow>
                        {props.tags}
                      </BTagsGridNew>
                      <FlexBox offsetTop={52}>
                        {props.status}
                      </FlexBox>
                    </FlexBox>
                  )}
                </FlexBox>
              ) : (
                <FlexBox offsetTop={isMobile ? 24 : 24}>
                  {props.isLoading ? (
                    <Skeleton
                      style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                      width={wx}
                      height={32}
                    />
                  ) : (
                    <BottomOuterMobile>
                      <div className="BottomOuterMobileLikes">
                        {props.likesSticker}
                      </div>

                      <div className="BottomOuterMobileActions">
                        <FlexBox directionMobile="row" gxMobile={12}>
                          {isTagsButtonVisible && <TagsMobBtn onClick={handleToggleMobileTagClick} flag={showMobileTags} />}
                        </FlexBox>
                      </div>
                    </BottomOuterMobile>
                  )}
                </FlexBox>
              )}
              {isBookMainPage && isMobile && (
                <FlexBox offsetTopMobile={isBookMainPage && isMobile && !is743 ? 'auto' : 0}>
                  {props.isLoading ? (
                    <Skeleton
                      style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                      width={wx}
                      height={32}
                    />
                  ) : (
                    <FlexBox direction='column' directionMobile='column' width={'100%'}>
                      {props.bottom}
                    </FlexBox>
                  )}
                </FlexBox>
              )}

              {(props.firstLine || props.isLoading) && (
                <FlexBox offsetTop={25}>
                  {props.isLoading ? (
                    <Skeleton
                      style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                      width={wx}
                      height={40}
                    />
                  ) : (
                    props.firstLine
                  )}
                </FlexBox>
              )}

              <FlexBox offsetTop={(props.isLoading || showMobileTags) ? 24 : 0}>
                {props.isLoading ? (
                  <Skeleton
                    style={{ backgroundColor: theme.skeleton.skeletonBgColor, marginBottom: '14px' }}
                    width={wx}
                    height={57}
                  />
                ) : (
                  <>
                    <MobBottomWrapper 
                      directionMobile="column"
                      widthMobile={'100%'} 
                      shouldMobOffset={
                        isGeograffee && 
                        props.point && 
                        !showMobileTags
                      }
                    >
                      {props.purchase}
                      {showMobileTags && (
                        <>
                          <FlexBox gx={16} gxMobile={8} width="100%">
                            <TagsMobile show={showMobileTags} scroll={collapseTags} background={tagsGradientColor}>
                              {props.tags}
                            </TagsMobile>
                            {props?.editorTags || null}
                          </FlexBox>

                      </>
                      )}
                      <RelatedProductsBlock {...relatedProductsProps} />
                      <MediiiaContextHomeBanner
                        hasProductsBelow={hasRelatedProducts}
                        project={props.project}
                      />
                      {isMaskSwitcherVisibile && <MaskSwitcher project={props?.project} minOffset={showMobileTags} />}
                    </MobBottomWrapper>
                  </>
                )}
              </FlexBox>
            </FlexBox>
          }
          leftSide={
            <ProjectLeftSectionOuterMobile isAgeytomesh={isAgeytomesh} isFtblMatch={isFtblMatch}>
                <MobileBookTitleOuter hide={!!isBookMainPage}>
                  {props?.contextName && props?.contextName}
                  <div className="ProjectHeaderOuterMobile">
                    {isBookChapter ? (
                      <FlexBox width={'100%'} offsetBottomMobile={16}>
                        <MainBookTitle book={props.project} />
                      </FlexBox>
                    ) : (
                      !isSuperPuper && (props.header || <ProjectHeader project={props.project} fireClick={isPreview} />)
                    )}
                    <div className="ProjectHeaderTitleMobile">
                      {props.title}
                    </div>
                  </div>
                </MobileBookTitleOuter>

              <ProjectImageWrapper isProjectPage={!!isBookMainPage}>
                {props.bookmarkButtonMobile}
                {props.placeElement && <div style={{position: 'absolute', right: '10px', top: '-3px'}}>{props.placeElement}</div>}
                {props.isLoading ? (
                  <Skeleton
                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                    width="100%"
                    height="100%"
                  />
                ) : (
                  props.image
                )}
              </ProjectImageWrapper>
            </ProjectLeftSectionOuterMobile>
          }
        />
      )}

      {showMobLayout && !props.project?.isBook && (
        <VoteLongreadBlockWrapper justify="center" align="center">
          <VoteBlock
            setShowVoteEndState={setShowVoteEndState}
            project={props.project}
            fullSize
            position={VOTE_BLOCK_POSITION.afterContainer}
          />
        </VoteLongreadBlockWrapper>
      )}

      {showMobLayout && shouldShowAfterContainerLayout && props.queueButtons && (
        <QueueButtonsAboveAfterContainerOuter direction="column" directionMobile="column">
          {props.queueButtons}
        </QueueButtonsAboveAfterContainerOuter>
      )}

      {showMobLayout && shouldShowAfterContainerLayout && (
        <AfterContainerLayoutOuter direction="column" directionMobile="column">
          <ProjectLongreadWrapperAfterContainer
            showTags={showMobileTags}
            vtype={viewType}
            isBooks={!!isBookMainPage || !!isBookProjectPage}
          >
            {props.afterContainerLayout}
          </ProjectLongreadWrapperAfterContainer>
        </AfterContainerLayoutOuter>
      )}

      {!props.isLoading && (
        <ProjectTranslatedBanner
          projectId={props.project?.projectId}
          isOwner={props.isOwner}
          //@ts-ignore
          autoTranslateStatus={props.project?.autoTranslateStatus}
        />
      )}

      {props.gameInfo}
      {showMobLayout && !isBookMainPage && <>
        <FlexBox width="100%" direction="column" directionMobile="column" align="center">
          {props.bookBuyProcessingComponent && <FlexBox width="calc(100% - 20px)" offsetBottom={16}>
            {props.bookBuyProcessingComponent}
          </FlexBox>}
          {props.bottom}
        </FlexBox>
      </>}

      {props.dialog && (
        <FlexBox width="100%" direction="column" directionMobile="column" align="center">
          <ProjectLongreadWrapper vtype={'container'} isBooks={!!isBookMainPage || !!isBookProjectPage}>
            <FlexBox width="100%" offsetTop={24} offsetTopMobile={16}>
              {props.dialog}
            </FlexBox>
          </ProjectLongreadWrapper>
        </FlexBox>
      )}
      {props.inPartExam}
      {props.studentProject}
      {!isFtbl && <FlexBox width="100%" offsetTopMobile={14} offsetBottomMobile={14} direction="column" directionMobile="column" align="center">
        {props.inPartCompetition}
      </FlexBox>}
      {!props.project?.isBook && !isFtbl && (
        <VoteLongreadBlockWrapper justify='center' align='center'>

            <VoteBlock setShowVoteEndState={setShowVoteEndState} project={props.project} fullSize position={VOTE_BLOCK_POSITION.longread} />

        </VoteLongreadBlockWrapper>
      )}

      {props?.queue && <QueueWrapper width="100%" offsetTop={24} justify="center">
        {props?.queue}
      </QueueWrapper>}

      {props.subscribeInfo && !isFtbl && (
        <FlexBox>
          {props.subscribeInfo}
        </FlexBox>
      )}

      <ProjectLongreadWrapper showTags={showMobileTags} vtype={viewType} isBooks={!!isBookMainPage || !!isBookProjectPage}>
        {props.isLoading ? <Skeleton style={{ marginLeft: 'auto', marginRight: 'auto' }} offsetTop={24} offsetTopMobile={8} width="100%" widthMobile={is743 ? 'calc(100% - 20px)' : '100%'} height={100}/> : props.longread}
        {props.LMSLongreads}
      </ProjectLongreadWrapper>

      {!isFtbl && <VoteLongreadBlockWrapper justify='center' align='center'>

          <VoteBlock setShowVoteEndState={setShowVoteEndState} project={props.project} fullSize position={VOTE_BLOCK_POSITION.bottom} />

      </VoteLongreadBlockWrapper>}
    </FlexBox>
  );
};

export default memo(ProjectDisplayLayout);
import { FC, memo, ReactElement, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { Colors, FlexBox, MEDIA, Skeleton, SPACING, useBreakpoint as useBreakpointClient } from '@brg/ui';
import styled, { useTheme, css } from 'styled-components';
import { Project } from '@brg/api';
import { AppTester } from '../../../supporting';
import { ProjectImageWrapper, ProjectLongreadWrapper } from '../../pages/project/styles';
import { ProjectContainerLayout } from './project-container-layout';
import ProjectHeader from '../../pages/project/components/ProjectHeader';
import TagsMobile from "../../pages/project/components/TagsMobile";
import { BTag } from "../../../core/components";
import { LayoutConst } from "../../../core";
import { useParams, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {CreateExternalLinkTo, LinkTo} from 'shared/constants/linkTo';
import { MainBookTitle } from '_deziiign/books/pages/BookView/components/MainBookTitle';
import { TagsMobBtn } from '../TagsMobBtn/TagsMobBtn';
import { useSelector } from "react-redux";
import { VOTE_BLOCK_POSITION } from 'shared/pages/project/sections/ps-bottom';
import VoteBlock from 'shared/pages/project/components/VoteBlock/VoteBlock';
import { MaskSwitcher } from 'shared/pages/project/components/MaskSwitcher';
import { useGetCard, useGetCards, useGetCardsList } from 'shared/hooks/api/useGetCards';
import {BTagsGridNew} from "../../../core/components/bTag/bTagsGridNew/bTagsGridNew";
import {isServer} from "../../constants/isServer";
import {useParamsSsr, useRouteMatchSsr} from "../../hooks/useRouteMatchSsr";
import { SP_VOTE_COLLECTION_SIGN } from 'shared/pages/VotesAdmin/constants';
import { BlockedProjectBanner } from 'shared/pages/project/components/BlockedProjectBanner';
import { isFtblMatchProject } from 'shared/constants/ftblProjectTypes';
import { RelatedProductsBlock, hasRelatedProductsContent } from './RelatedProductsBlock';
import { MediiiaContextHomeBanner } from './MediiiaContextHomeBanner';
import { ProjectTranslatedBanner } from 'shared/pages/project/components/ProjectTranslatedBanner';
import { getRepBaseByProvider } from 'shared/helpers/shopCardContentDisplayUrl';

//const TAGS_COUNT_IF_NEED_SCROLL = 7;

export interface ProjectDisplayLayoutProps {
  image?: ReactElement;
  firstLine?: ReactElement;
  contextName?: ReactElement;
  canViewStatus?: boolean;
  title?: ReactElement;
  header?: ReactElement;
  tags?: ReactElement[] | ReactElement;
  editorTags?: ReactElement;
  purchase?: ReactElement;
  point?: ReactElement;
  team?: ReactElement;
  dialog?: ReactElement;
  longread?: ReactElement;
  moreTitle?: ReactElement;
  studentProject?: ReactElement;
  more?: ReactElement;
  bottom?: ReactElement;
  afterContainerLayout?: ReactElement;
  likesSticker?: ReactElement;
  inPartCompetition?: ReactElement;
  inPartExam?: ReactElement;
  status?: ReactElement;
  placeElement?: ReactElement;
  protectStatus?: ReactElement;
  bookmarkButtonMobile?: ReactElement;
  subscribeInfo?: ReactElement;
  gameInfo?: ReactElement
  queue?: ReactElement
  LMSLongreads?: ReactElement
  queueButtons?: ReactElement
  bookBuyProcessingComponent?: ReactElement
  className?: any;
  isLoading?: boolean;
  style?: any;
  project: Project;
  shouldHideTags?: boolean;
  isCollectionVote?: boolean;
  mobileOffset?: number;
  blockedState?: {
    isBlocked: boolean;
    reasons: number[];
    otherReason: string;
  };
  statusVisible?: boolean;
  showTwoColumnsCriterias?: boolean;
  isOwner?: boolean;
  bgColor?: string;
  isLongTitle?: boolean;
  setShowVoteEndState?: () => void;
}

const SHORT_TITLE_TAGS_WINDOW_HEIGHT = 114;
const SHORT_TITLE_TAGS_WINDOW_HEIGHT_SMALL = 98;
const LONG_TITLE_TAGS_WINDOW_HEIGHT = 72;
const LONG_TITLE_TAGS_WINDOW_HEIGHT_SMALL = 62;
const LONG_TITLE_TAGS_WINDOW_HEIGHT_BELOW_1200 = 98;

const ProjectTitleSection = styled(FlexBox)`
  margin-bottom: 24px;
    
  @media(max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      margin-bottom: 16px;
  }
`;

const ProjectLeftSectionOuterMobile = styled.div<{ isAgeytomesh?: boolean; isFtblMatch?: boolean }>`
  position: relative;
  height: 100%;

  .ProjectHeaderOuterMobile {
    width: 100%;
  }

  .ProjectHeaderTitleMobile {
    font-weight: 500;
    font-size: 24px;
    line-height: 26px;
    padding: 0 10px;
    margin-top: ${({ isFtblMatch }) => isFtblMatch ? SPACING.S_8 : SPACING.S_10}px;

    @media(max-width: ${LayoutConst.screen.md}px) {
      padding-left: 0;

      ${({ isAgeytomesh }) => isAgeytomesh ? css`
        color: #545453;
        font-family: 'Inter';
        font-style: normal;
        font-weight: 500;
        font-size: 22px;
        line-height: 28px;
    ` : null};

    }
  }
`;

const MobileBookTitleOuter = styled(FlexBox)<{ hide?: boolean }>`
  display: flex;
  @media(max-width: ${LayoutConst.screen.md}px) {
    display: ${({ hide }) => hide ? 'none': "flex"};
  }
  @media(max-width: ${LayoutConst.screen.s - 1}px) {
    display: flex;
  }
`;

export const VoteLongreadBlockWrapper = styled(FlexBox)`
  width: 100%;
  margin: 0 auto;
  max-width: none;

  @media(max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    max-width: 888px;
  }

  @media(max-width: ${LayoutConst.screen.md}px) {
    width: calc(100% - 32px);
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
  }
`;

export const ProjectLongreadWrapperAfterContainer = styled(ProjectLongreadWrapper)`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
`;

const QueueButtonsAboveAfterContainerOuter = styled(FlexBox)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const AfterContainerLayoutOuter = styled(FlexBox)`
  display: none;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.l}px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;


export const BottomOuterMobile = styled.div`
  display: flex;
  width: 100%;

  .BottomOuterMobileLikes > div {
    flex-direction: row;
    align-items: center;
    gap: 16px;

    > div {
      margin-left: 0;
    }

    @media(max-width: ${LayoutConst.screen.md}px) {
      gap: 12px;
    }

    .buttons-wrapper {
      flex-direction: row;
      align-items: center;
      gap: 12px;

      .ps-action-btn {
        margin-left: 0 !important;
        margin-bottom: 0;
      }
    }

    .ShareButtonWrapper {
      margin-left: 0 !important;
    }
  }

  .StickyActionBarLikesControllerWrapper {
    margin-right: 8px;
  }

  .StickyActionBarShareButtonWrapper {
    margin-right: 4px;
  }

  .BottomOuterMobileActions {
    margin-left: auto;

    .MoreButtonWrapper {
      display: flex;
      padding: 0;
    }
  }
`;

export const QueueWrapper = styled(FlexBox)`
  @media(max-width: ${LayoutConst.screen.s -1 }px) {
    padding: 0 10px;
  }
`;

const RelatedTitle = styled.div`
    font-size: 20px;
    font-weight: 600;
    line-height: 25px; /* 125% */
    letter-spacing: -0.4px;
    color: ${({ theme }) => theme.relatedTitle};
    margin-top: 32px;
    margin-bottom: 16px;
    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
        font-size: 17px;
        line-height: 22px; /* 129.412% */
        letter-spacing: -0.34px; 
        margin-bottom: 8px;
        margin-top: 0px;  
    }

`;

export const MobBottomWrapper = styled(FlexBox)<{ shouldMobOffset?: boolean }>`
  width: 100%;

  @media(${MEDIA.MOBILE}) {
    margin-left: 0;
  }
    ${({ shouldMobOffset }) => 
      shouldMobOffset ? css`
        > div {
          padding-top: 24px;
        }
    ` : null};
`

const safeUseParams = isServer ? useParamsSsr : useParams
const useSafeRouteMatch = isServer ? useRouteMatchSsr : useRouteMatch
const useParamsProps = isServer ? LinkTo.project : undefined

const useBreakpoint = isServer ? () => false : useBreakpointClient;

const ProjectDisplayLayout: FC<ProjectDisplayLayoutProps> = (props) => {
  const {project, blockedState, setShowVoteEndState} = props
  const isMobile = useBreakpoint(940);
  const is834 = useBreakpoint(834);
  const is744 = useBreakpoint(LayoutConst.screen.s);
  const is743 = useBreakpoint(LayoutConst.screen.s - 1);
  const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
  const is1200 = useBreakpoint(LayoutConst.screen.l_12)
  const isBelow1200 = useBreakpoint(LayoutConst.screen.l);
  const theme = useTheme();
  const projectIdRef = useRef<{ id: string }>(null);
  const rightSideRef = useRef<HTMLDivElement>(null);
  const tagsContainerRef = useRef<HTMLDivElement>(null);
  const { id } = safeUseParams(useParamsProps);
  const isDeziiign = AppTester.isDeziiign();
  const [collapseTags, setCollapseTags] = useState(false);
  const [showMobileTags, setShowMobileTags] = useState(false);
  const isBookMainPage = useSafeRouteMatch('/books/book');
  const isBookProjectPage = useSafeRouteMatch('/book');
  const isBookPreview = useSafeRouteMatch('/books/preview');
  const isGeograffee = AppTester.isGeograffee();
  const isOnlines = AppTester.isOnlines();
  const isBooksGallllery = AppTester.isBooksGallllery()

  const isFtbl = AppTester.isFtbl()
  //TODO: [МУСОР] разобраться и удалить
  // const isBookEditPage = window.location.pathname.startsWith("/books/book-edit") || (AppTester.isBooksMain() && window.location.pathname.startsWith("/book-edit"))
  const isBookEditPage = false;
  const vtype = useSelector((state: any) => state.vt.projectVType);
  const { t, i18n } = useTranslation();

  const isProjectCardId = useMemo(() => {
      //@ts-ignore
    return props.project?.entitiesCreators?.[0]?.entityId ?? null;
  }, [props.project]);

  const hasProductCard = useMemo(() => project?.includedCards, [project]);
  //@ts-ignore
  const isReproduction = useMemo(() => project?.includedReproductionModels, [project]); 
  const isCardOfReproduction = isReproduction?.[0]?.cardId || '';

  const { card } = useGetCard(isCardOfReproduction || isProjectCardId);
  const { cards: allCards } = useGetCardsList(hasProductCard, !!hasProductCard)
  
  //@ts-ignore
  const isBookProject = useMemo(() => (props.project?.isBook && props.project?.paymentType === 1), [])

  const cards = useMemo(() => isBookProject && !isBooksGallllery? [{name: 'Электронная книга', cardId: props.project?.projectId}] : allCards ?? [], [allCards]);

  //TODO: добавить поля в интерфейс Project в api, чтобы избавиться от этих тс игноров 
  

  //@ts-ignore 
  const includedCollection = useMemo(() => project?.includedCardCollectionsIds, [project]);

  //@ts-ignore
  const includedCalendar = useMemo(() => project?.includedCalendarIds, [project]);

  const isTagsButtonVisible = useMemo(() => Array.isArray(props?.tags) ? props?.tags?.length > 0 : 3, [props?.tags])

  const isBook = useMemo(() => (isBookMainPage) , [isBookMainPage ])

  const isFtblMatch = useMemo(
    () => isFtblMatchProject(props.project?.projectType),
    [props.project?.projectType],
  );

  const smHeight = useMemo(() => (window.innerHeight < LayoutConst.screen.height.projectCover), [])

  const longTitleTagWindowHeightSmall = useMemo(() => {
    if (!props.isLongTitle) return undefined;
    return isBelow1200
      ? LONG_TITLE_TAGS_WINDOW_HEIGHT_BELOW_1200
      : LONG_TITLE_TAGS_WINDOW_HEIGHT_SMALL;
  }, [props.isLongTitle, isBelow1200]);

  const maxAllowedTagHeight = useMemo(() => {
    if (props.isLongTitle) {
      if (isBelow1200) return LONG_TITLE_TAGS_WINDOW_HEIGHT_BELOW_1200;
      if (isMac13 || smHeight) return LONG_TITLE_TAGS_WINDOW_HEIGHT_SMALL;
      return LONG_TITLE_TAGS_WINDOW_HEIGHT;
    }
    if (isMac13 || smHeight) return SHORT_TITLE_TAGS_WINDOW_HEIGHT_SMALL;
    return SHORT_TITLE_TAGS_WINDOW_HEIGHT;
  }, [props.isLongTitle, isBelow1200, isMac13, smHeight]);

  const collapsedTagWindowHeight = props.isLongTitle
    ? LONG_TITLE_TAGS_WINDOW_HEIGHT
    : SHORT_TITLE_TAGS_WINDOW_HEIGHT;

  const collapsedTagWindowHeightSmall = props.isLongTitle
    ? longTitleTagWindowHeightSmall
    : SHORT_TITLE_TAGS_WINDOW_HEIGHT_SMALL;

  const wx = useMemo(() =>  rightSideRef?.current?.clientWidth || '100%' , [rightSideRef, rightSideRef.current, isMac13, is743, isMobile, smHeight ])


  const handleOpenCardPage = () => {
    if (isReproduction?.length) {
      const baseUrl = getRepBaseByProvider(isReproduction?.[0]?.cardProviderId);
      if (!baseUrl) return;
      window.open(`${baseUrl}/typography/${isReproduction[0]?.cardId}`, '_blank');
    }
  };

  const handleOpenCalendarPage = () => {
    
    if (includedCalendar?.length) {
      window.open(`${process.env.REACT_APP_URL_GALLERY}/365/calendar/preview/${includedCalendar[0]}`, '_blank');
    }
  };

  const handleOpenCollectionPage = () => {
    
    if (includedCollection?.length) {
      window.open(`${process.env.REACT_APP_URL_GALLERY}/catalog/${includedCollection[0]}`, '_blank');
    }
  };

  const handleOpenProductPage = (card: any) : string => {
    if (isBookProject) {
      return CreateExternalLinkTo.mediiiaBooks(`/book/${props.project?.projectId}`)
    }
    if (hasProductCard?.length) {
      const cardId = card.entityId || card.cardId
      const baseUrl = `${process.env.REACT_APP_URL_ARTZ_SHOP}/shop`
      let fullUrl
      if (card?.shopDomain) {
        fullUrl = `https://${card.shopDomain}/card/${cardId}`
      } else {
        fullUrl = `${baseUrl}/card/${cardId}`
      }
      return fullUrl
    }
    return ''
  };

  const formatSequenceNumber = (sequenceNumber?: string): string => {
    if (!sequenceNumber) return '';
    
    const letters = sequenceNumber.match(/^[A-Za-z]+/)?.[0] || '';
    const digits = sequenceNumber.replace(letters, '').substring(0, 4);
    
    return `${letters} ${digits}`; 
  };

  const viewType = vtype || 'container';

  const shouldShowAfterContainerLayout = useMemo(
    () => (
      !!props.afterContainerLayout
      && !['fluid-wide', 't-shape', 'fixed-ratio-large', 'free-type'].includes(String(viewType))
    ),
    [props.afterContainerLayout, viewType],
  );

  const isPreview = !!useRouteMatch("/preview");

  const isMaskSwitcherVisibile = useMemo(
    () => (
      (props.project?.book && 
        !!props.project?.book?.isProtected && 
        (isPreview || isBookPreview) && 
        !!props.canViewStatus)
      ) , [props, isPreview, isBookPreview])

  const isAgeytomesh = AppTester.isAgeytomesh();
  const isDesign = AppTester.isDeziiign();
  const isHseDesign = AppTester.isHSEDesign();
  const isSuperPuper = AppTester.isSuperPuper();
  const isCGrave = AppTester.isCGRAVE();
  const isAdcrDafesNet = AppTester.isAdcrDafesNet();
  const isContinuumSite = AppTester.isContinuumSite();
  const isCompetition = AppTester.isCompetition();
  const isDafes = AppTester.isGeoDafes();

  useEffect(() => {
    if (props.isLoading) return;

    setCollapseTags(false);

    const getTagsMeasureElement = (): HTMLElement | null => {
      if (is743) {
        return document.querySelector('#mobile-tags-container') as HTMLElement | null;
      }

      return (
        (tagsContainerRef.current?.querySelector('#tags_grid_wrapper') as HTMLElement | null)
        ?? tagsContainerRef.current
      );
    };

    const measureTagsOverflow = () => {
      const tagsElement = getTagsMeasureElement();
      if (!tagsElement) return;

      setCollapseTags(tagsElement.scrollHeight > maxAllowedTagHeight + 1);
    };

    let rafId1 = 0;
    let rafId2 = 0;
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(measureTagsOverflow);
    });

    if (!is743) {
      [100, 300, 600, 1000].forEach((delay) => {
        timeoutIds.push(setTimeout(measureTagsOverflow, delay));
      });
      void document.fonts?.ready.then(() => {
        if (!cancelled) {
          measureTagsOverflow();
        }
      });
    }

    window.addEventListener('resize', measureTagsOverflow);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId1);
      cancelAnimationFrame(rafId2);
      timeoutIds.forEach(clearTimeout);
      window.removeEventListener('resize', measureTagsOverflow);
    };
  }, [
    id,
    is743,
    maxAllowedTagHeight,
    props.tags,
    props.isLongTitle,
    showMobileTags,
    props.isLoading,
  ]);


  //@ts-ignore
  projectIdRef.current = id;

  const handleToggleMobileTagClick = useCallback((flag: boolean) => setShowMobileTags(flag),  [])

  const calculateGradientColor = useMemo(() => {
    if (props.bgColor) {
      return props.bgColor
    }
    if (isSuperPuper || isCGrave || isContinuumSite || isAdcrDafesNet) {
      return '#000000';
    }
    if (isDesign || isHseDesign || isCompetition || isDafes ) {
      if (isBookEditPage || isMobile) return Colors.gal
      //@ts-ignore
      return props?.project?.additionalInfo?.color || Colors.gal
    }
    return Colors.geo; 
  }, [props?.project, isHseDesign, isDesign, isCompetition, isCGrave, isAdcrDafesNet , isContinuumSite, isMobile])

  const tagsGradientColor = calculateGradientColor;
  const relatedProductsProps = useMemo(
    () => ({
      cards,
      isReproduction,
      includedCollection,
      includedCalendar,
      isBookProject,
      shadowColor: tagsGradientColor,
      theme,
      t,
      i18n,
      handleOpenProductPage,
      handleOpenCardPage,
      handleOpenCollectionPage,
      handleOpenCalendarPage,
      formatSequenceNumber,
    }),
    [
      cards,
      isReproduction,
      includedCollection,
      includedCalendar,
      isBookProject,
      tagsGradientColor,
      theme,
      t,
      i18n,
      handleOpenProductPage,
      handleOpenCardPage,
      handleOpenCollectionPage,
      handleOpenCalendarPage,
      formatSequenceNumber,
    ],
  );
  const hasRelatedProducts = useMemo(
    () => hasRelatedProductsContent(relatedProductsProps),
    [relatedProductsProps],
  );
  const showHeaderOnTop = useMemo(() => (!props?.isCollectionVote), [props?.isCollectionVote])
  const isBookChapter = useMemo(() => (isBookPreview || props?.project?.book || (isPreview && !!props.project?.book)), [isBookPreview, isPreview, props.project])

  //Уберу , когда закончу с книгами @Artem
  const showDespLayout = useMemo(() => {
    if (isOnlines && !isMobile) return true;
    if (!isOnlines && !is743) return true;
    return false
  }, [isMobile, isOnlines, is744, is743, is834, isBook])


  //Уберу , когда закончу с книгами @Artem
  const showMobLayout = useMemo(() => {
    if (isOnlines && isMobile) return true;
    if (!isOnlines && is743) return true;
    return false
  }, [isOnlines, isMobile, is744, is834, is743, isBook ])
    const isCollectionVote = project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN)

  return (
    <FlexBox direction="column" directionMobile="column">
        {props.project && <BlockedProjectBanner 
          project={props.project} 
          isTemporaryBlocked={blockedState?.isBlocked}
          temporaryBlockingReasons={blockedState?.reasons}
          temporaryOtherReason={blockedState?.otherReason}
        />}
        {showDespLayout && (
        <ProjectContainerLayout
          footer={
            is744 && !isFtbl ?
            <>
                    <MobBottomWrapper 
                      directionMobile="column"
                      widthMobile={'100%'}
                      offsetLeftMobile={16}
                      shouldMobOffset={
                        isGeograffee && 
                        props.point && 
                        !showMobileTags
                      }
                    >
                            <RelatedProductsBlock {...relatedProductsProps} />
                      {isMaskSwitcherVisibile && <MaskSwitcher project={props?.project} minOffset={showMobileTags} />}
                    </MobBottomWrapper>
            </> :
            undefined
          }
          isProjectPage={!isBookMainPage && !isOnlines}
          isBookMainPage={!!isBookMainPage}
          isCollectionVote={isCollectionVote}
          header={props.header || <>
            {showHeaderOnTop && 
              <ProjectHeader 
                project={props.project} 
                fireClick={isPreview} 
                isProjectPage={!isBookMainPage && !isOnlines} 
                isBookPage={!!isBookMainPage} 
              />}
            </>}
          rightSide={
            <FlexBox
              direction="column"
              directionMobile="column"
              justify="space-between"
              width="100%"
              ref={rightSideRef}
              height="100%"
              style={{position: 'relative'}}
            >
              {!isMobile && props.placeElement && <div style={{position: 'absolute', right: is1200 ? '-34px' : '-64px'}}>{props.placeElement}</div>}
              <FlexBox direction="column" directionMobile='column'>
                {props?.bookBuyProcessingComponent ? props?.bookBuyProcessingComponent :  isDeziiign ? (
                  <>
                    <ProjectTitleSection gy={isBookChapter ? 0 : 8} direction={'column'} directionMobile='column' justify="space-between" align="flex-start">
                      {isBookChapter && (
                        <FlexBox width={'100%'} offsetBottom={16}>
                          <MainBookTitle book={props.project} />
                        </FlexBox>
                      )}
                      {props.isLoading ? (
                        <Skeleton
                          style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                          width={wx}
                          height={64}
                        />
                      ) : (
                        <FlexBox>
                          {props.title}
                        </FlexBox>
                      )}
                      {props?.firstLine && (
                        <FlexBox>
                            {props.firstLine}
                        </FlexBox>
                      )}
                    </ProjectTitleSection>

                    {props.isLoading ? (
                      <Skeleton
                        style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                        width={wx}
                        height={57}
                      />
                    ) : (
                        <>
                          <FlexBox direction='column' directionMobile='column' style={{ position: 'relative' }}>
                            <FlexBox gx={16} gxMobile={8} width="100%">
                              <div ref={tagsContainerRef} style={{ width: '100%' }}>
                                <BTagsGridNew
                                  id="tags_container"
                                  gap={10}
                                  scroll={collapseTags}
                                  shadow={collapseTags}
                                  isProjectPage
                                  noLimitMaxHeight={!collapseTags}
                                  tagWindowHeight={collapseTags ? collapsedTagWindowHeight : undefined}
                                  tagWindowHeightSmall={collapseTags ? collapsedTagWindowHeightSmall : undefined}
                                >
                                  {props.tags}
                                </BTagsGridNew>
                              </div>
                              {props?.editorTags || null}
                            </FlexBox>
                            {props.protectStatus}
                          </FlexBox>
                          {!is744 && <RelatedProductsBlock {...relatedProductsProps} />}
                          <MediiiaContextHomeBanner
                            hasProductsBelow={hasRelatedProducts && !is744}
                            project={props.project}
                          />
                        </>
                    )}
                  </>
                ) : (
                  <>
                    <FlexBox offsetBottom={(isMac13 && !isBookMainPage) ? 16 : !!isBookMainPage ? 24 : 16} gx={0} direction={'column'} directionMobile='column' justify="space-between" align="flex-start">
                      {isBookChapter && (
                        <FlexBox width={'100%'} offsetBottom={16}>
                          <MainBookTitle book={props.project} />
                        </FlexBox>
                      )}
                      <FlexBox offsetBottom={24} gx={8} justify="space-between" align="flex-start" width={'100%'}>
                        {props.isLoading ? (
                          <Skeleton
                            style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                            width={wx}
                            height={64}
                          />
                        ) : (
                          <FlexBox>
                            {props.title}
                          </FlexBox>
                        )}
                        {props?.firstLine && (
                          <FlexBox>
                            {props.firstLine}
                          </FlexBox>
                        )} 
                      </FlexBox>
                      <FlexBox width={'100%'}>
                        {props.isLoading ? (
                          <Skeleton
                            style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                            width={wx}
                            height={57}
                          />
                        ) : (
                          <FlexBox direction='column'>
                          {/*@ts-ignore*/}
                            <BTagsGridNew id={'tags_container'} gap={10} scrollMode="all" shadow>
                              {props.tags}
                            </BTagsGridNew>
                            {props.protectStatus}
                            {!!props.statusVisible && (
                              <FlexBox offsetTop={52}>
                                {props.status}
                              </FlexBox>
                            )}
                          </FlexBox>
                        )}
                      </FlexBox>
                      {!is744 && <RelatedProductsBlock {...relatedProductsProps} />}
                    </FlexBox>
                  </>
                )}
              </FlexBox>
              <FlexBox>
                {props.isLoading ? (
                  <Skeleton
                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                    width={wx}
                    height={32}
                  />
                ) : (
                  <FlexBox direction={isMaskSwitcherVisibile ? 'row' : 'column'} directionMobile={isMaskSwitcherVisibile ? 'row' : 'column'} width={'100%'}>
                    {props.bottom}
                    {isMaskSwitcherVisibile && <MaskSwitcher project={props?.project} />}
                  </FlexBox>
                )}
              </FlexBox>
            </FlexBox>
          }
          leftSide={
            <ProjectImageWrapper
              isProjectPage={!isBookMainPage && !isOnlines}
              isBooks={!!isBookMainPage}
              isFtblMatch={isFtblMatch}
            >
              {isMobile && props.placeElement && <div style={{position: 'absolute', left: '10px', top: '-3px'}}>{props.placeElement}</div>}
              {props.isLoading ? (
                <Skeleton
                  style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                  width="100%"
                  height="100%"
                />
              ) : (
                props.image
              )}
            </ProjectImageWrapper>
          }
        />
      )}

      {showDespLayout && !props.project?.isBook && (
        <VoteLongreadBlockWrapper justify="center" align="center">
          <VoteBlock
            setShowVoteEndState={setShowVoteEndState}
            project={props.project}
            fullSize
            position={VOTE_BLOCK_POSITION.afterContainer}
          />
        </VoteLongreadBlockWrapper>
      )}

      {showDespLayout && shouldShowAfterContainerLayout && props.queueButtons && (
        <QueueButtonsAboveAfterContainerOuter direction="column" directionMobile="column">
          {props.queueButtons}
        </QueueButtonsAboveAfterContainerOuter>
      )}

      {showDespLayout && shouldShowAfterContainerLayout && (
        <AfterContainerLayoutOuter direction="column" directionMobile="column">
          <ProjectLongreadWrapperAfterContainer
            showTags={showMobileTags}
            vtype={viewType}
            isBooks={!!isBookMainPage || !!isBookProjectPage}
          >
            {props.afterContainerLayout}
          </ProjectLongreadWrapperAfterContainer>
        </AfterContainerLayoutOuter>
      )}

      {showMobLayout && (
        <ProjectContainerLayout
          isBookMainPage={!!isBookMainPage}
          rightSide={

            <FlexBox direction="column" directionMobile="column" width="100%" ref={rightSideRef}>
              {isBookMainPage && isMobile && !is743 && (
                <div className="ProjectHeaderOuterMobile">
                  <div className="ProjectHeaderTitleMobile">
                    {props.title}
                  </div>
                </div>
              )}
              {!!isBookMainPage && isMobile && !is743 ? (
                <FlexBox offsetTopMobile={24}>
                  {props.isLoading ? (
                    <Skeleton
                      style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                      width={wx}
                      height={57}
                    />
                  ) : (
                    <FlexBox direction='column'>
                      <BTagsGridNew id="tags_container" scrollMode="all" shadow>
                        {props.tags}
                      </BTagsGridNew>
                      <FlexBox offsetTop={52}>
                        {props.status}
                      </FlexBox>
                    </FlexBox>
                  )}
                </FlexBox>
              ) : (
                <FlexBox offsetTop={isMobile ? 24 : 24}>
                  {props.isLoading ? (
                    <Skeleton
                      style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                      width={wx}
                      height={32}
                    />
                  ) : (
                    <BottomOuterMobile>
                      <div className="BottomOuterMobileLikes">
                        {props.likesSticker}
                      </div>

                      <div className="BottomOuterMobileActions">
                        <FlexBox directionMobile="row" gxMobile={12}>
                          {isTagsButtonVisible && <TagsMobBtn onClick={handleToggleMobileTagClick} flag={showMobileTags} />}
                        </FlexBox>
                      </div>
                    </BottomOuterMobile>
                  )}
                </FlexBox>
              )}
              {isBookMainPage && isMobile && (
                <FlexBox offsetTopMobile={isBookMainPage && isMobile && !is743 ? 'auto' : 0}>
                  {props.isLoading ? (
                    <Skeleton
                      style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                      width={wx}
                      height={32}
                    />
                  ) : (
                    <FlexBox direction='column' directionMobile='column' width={'100%'}>
                      {props.bottom}
                    </FlexBox>
                  )}
                </FlexBox>
              )}

              {(props.firstLine || props.isLoading) && (
                <FlexBox offsetTop={25}>
                  {props.isLoading ? (
                    <Skeleton
                      style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                      width={wx}
                      height={40}
                    />
                  ) : (
                    props.firstLine
                  )}
                </FlexBox>
              )}

              <FlexBox offsetTop={(props.isLoading || showMobileTags) ? 24 : 0}>
                {props.isLoading ? (
                  <Skeleton
                    style={{ backgroundColor: theme.skeleton.skeletonBgColor, marginBottom: '14px' }}
                    width={wx}
                    height={57}
                  />
                ) : (
                  <>
                    <MobBottomWrapper 
                      directionMobile="column"
                      widthMobile={'100%'} 
                      shouldMobOffset={
                        isGeograffee && 
                        props.point && 
                        !showMobileTags
                      }
                    >
                      {props.purchase}
                      {showMobileTags && (
                        <>
                          <FlexBox gx={16} gxMobile={8} width="100%">
                            <TagsMobile show={showMobileTags} scroll={collapseTags} background={tagsGradientColor}>
                              {props.tags}
                            </TagsMobile>
                            {props?.editorTags || null}
                          </FlexBox>

                      </>
                      )}
                      <RelatedProductsBlock {...relatedProductsProps} />
                      <MediiiaContextHomeBanner
                        hasProductsBelow={hasRelatedProducts}
                        project={props.project}
                      />
                      {isMaskSwitcherVisibile && <MaskSwitcher project={props?.project} minOffset={showMobileTags} />}
                    </MobBottomWrapper>
                  </>
                )}
              </FlexBox>
            </FlexBox>
          }
          leftSide={
            <ProjectLeftSectionOuterMobile isAgeytomesh={isAgeytomesh} isFtblMatch={isFtblMatch}>
                <MobileBookTitleOuter hide={!!isBookMainPage}>
                  {props?.contextName && props?.contextName}
                  <div className="ProjectHeaderOuterMobile">
                    {isBookChapter ? (
                      <FlexBox width={'100%'} offsetBottomMobile={16}>
                        <MainBookTitle book={props.project} />
                      </FlexBox>
                    ) : (
                      !isSuperPuper && (props.header || <ProjectHeader project={props.project} fireClick={isPreview} />)
                    )}
                    <div className="ProjectHeaderTitleMobile">
                      {props.title}
                    </div>
                  </div>
                </MobileBookTitleOuter>

              <ProjectImageWrapper isProjectPage={!!isBookMainPage}>
                {props.bookmarkButtonMobile}
                {props.placeElement && <div style={{position: 'absolute', right: '10px', top: '-3px'}}>{props.placeElement}</div>}
                {props.isLoading ? (
                  <Skeleton
                    style={{ backgroundColor: theme.skeleton.skeletonBgColor }}
                    width="100%"
                    height="100%"
                  />
                ) : (
                  props.image
                )}
              </ProjectImageWrapper>
            </ProjectLeftSectionOuterMobile>
          }
        />
      )}

      {showMobLayout && !props.project?.isBook && (
        <VoteLongreadBlockWrapper justify="center" align="center">
          <VoteBlock
            setShowVoteEndState={setShowVoteEndState}
            project={props.project}
            fullSize
            position={VOTE_BLOCK_POSITION.afterContainer}
          />
        </VoteLongreadBlockWrapper>
      )}

      {showMobLayout && shouldShowAfterContainerLayout && props.queueButtons && (
        <QueueButtonsAboveAfterContainerOuter direction="column" directionMobile="column">
          {props.queueButtons}
        </QueueButtonsAboveAfterContainerOuter>
      )}

      {showMobLayout && shouldShowAfterContainerLayout && (
        <AfterContainerLayoutOuter direction="column" directionMobile="column">
          <ProjectLongreadWrapperAfterContainer
            showTags={showMobileTags}
            vtype={viewType}
            isBooks={!!isBookMainPage || !!isBookProjectPage}
          >
            {props.afterContainerLayout}
          </ProjectLongreadWrapperAfterContainer>
        </AfterContainerLayoutOuter>
      )}

      {!props.isLoading && (
        <ProjectTranslatedBanner
          projectId={props.project?.projectId}
          isOwner={props.isOwner}
          //@ts-ignore
          autoTranslateStatus={props.project?.autoTranslateStatus}
        />
      )}

      {props.gameInfo}
      {showMobLayout && !isBookMainPage && <>
        <FlexBox width="100%" direction="column" directionMobile="column" align="center">
          {props.bookBuyProcessingComponent && <FlexBox width="calc(100% - 20px)" offsetBottom={16}>
            {props.bookBuyProcessingComponent}
          </FlexBox>}
          {props.bottom}
        </FlexBox>
      </>}

      {props.dialog && (
        <FlexBox width="100%" direction="column" directionMobile="column" align="center">
          <ProjectLongreadWrapper vtype={'container'} isBooks={!!isBookMainPage || !!isBookProjectPage}>
            <FlexBox width="100%" offsetTop={24} offsetTopMobile={16}>
              {props.dialog}
            </FlexBox>
          </ProjectLongreadWrapper>
        </FlexBox>
      )}
      {props.inPartExam}
      {props.studentProject}
      {!isFtbl && <FlexBox width="100%" offsetTopMobile={14} offsetBottomMobile={14} direction="column" directionMobile="column" align="center">
        {props.inPartCompetition}
      </FlexBox>}
      {!props.project?.isBook && !isFtbl && (
        <VoteLongreadBlockWrapper justify='center' align='center'>

            <VoteBlock setShowVoteEndState={setShowVoteEndState} project={props.project} fullSize position={VOTE_BLOCK_POSITION.longread} />

        </VoteLongreadBlockWrapper>
      )}

      {props?.queue && <QueueWrapper width="100%" offsetTop={24} justify="center">
        {props?.queue}
      </QueueWrapper>}

      {props.subscribeInfo && !isFtbl && (
        <FlexBox>
          {props.subscribeInfo}
        </FlexBox>
      )}

      <ProjectLongreadWrapper showTags={showMobileTags} vtype={viewType} isBooks={!!isBookMainPage || !!isBookProjectPage}>
        {props.isLoading ? <Skeleton style={{ marginLeft: 'auto', marginRight: 'auto' }} offsetTop={24} offsetTopMobile={8} width="100%" widthMobile={is743 ? 'calc(100% - 20px)' : '100%'} height={100}/> : props.longread}
        {props.LMSLongreads}
      </ProjectLongreadWrapper>

      {!isFtbl && <VoteLongreadBlockWrapper justify='center' align='center'>

          <VoteBlock setShowVoteEndState={setShowVoteEndState} project={props.project} fullSize position={VOTE_BLOCK_POSITION.bottom} />

      </VoteLongreadBlockWrapper>}
    </FlexBox>
  );
};

export default memo(ProjectDisplayLayout);
